import { StyledAlert } from "App/global-style";
import { CoveragePriceCalculation } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.types";
import { useTexts } from "Components/TextContext";
import { CarCoverage } from "Store/interfaces/products/coverage";
import React from "react";
import { Column } from "./styled";

interface Props {
  readonly coverages: CoveragePriceCalculation[];
}

export const CarCoverageMissingReasons = ({
  coverages,
}: Props): React.ReactElement => {
  const { getText } = useTexts();

  const availableCoverages = coverages.map((coverage) => {
    return coverage[0] as CarCoverage;
  });
  const reasons = coverages
    .map((coverage) => {
      return coverage[1]?.notAvailableReasons ?? [];
    })
    .flat();

  const hasMortgagee = reasons.includes("HasMortgage");

  const isOlderThanMaxForSuper =
    checkIsOlderThanMaxForSuper(availableCoverages);
  const cannotHaveLiabilityAndReducedHasMortgagee =
    checkCannotHaveLiabilityAndReducedHasMortgagee(
      availableCoverages,
      hasMortgagee,
    );
  const cannotHaveLiabilityAndReduced = checkCannotHaveLiabilityAndReduced(
    availableCoverages,
    hasMortgagee,
  );
  const cannotGetLiability = checkCannotGetLiability(
    availableCoverages,
    reasons,
    hasMortgagee,
  );
  const isLeasing = checkIsLeasing(reasons);

  // TODO This is temporary. Will not be necessary when SSX-2008 is completed
  const hasRental = availableCoverages.includes(
    CarCoverage.AUTO_FULL_OWN_DAMAGE_INCLUDING_RENTALCAR,
  );

  return (
    <Column>
      {cannotHaveLiabilityAndReducedHasMortgagee && (
        <StyledAlert title={getText("carform.alerttitle.loanoncar")} />
      )}

      {cannotHaveLiabilityAndReduced && !hasRental && (
        <StyledAlert
          title={getText("carform.alerttitle.nodelkaskoansvarnewcar")}
        />
      )}

      {isOlderThanMaxForSuper && (
        <StyledAlert title={getText("carform.alerttitle.nosuperoldcar")} />
      )}

      {isLeasing && (
        <StyledAlert title={getText("carform.alerttitle.leasedcar")} />
      )}

      {cannotGetLiability && (
        <StyledAlert title={getText("carform.alerttitle.noansvar")} />
      )}
    </Column>
  );
};

const carSuperCoverages = [
  CarCoverage.AUTO_FULL_SUPER_V2,
  CarCoverage.AUTO_FULL_SUPER_V2_INCLUDING_RENTALCAR,
];

const checkIsOlderThanMaxForSuper = (
  availableCoverages: CarCoverage[],
): boolean => {
  return !carSuperCoverages.some((coverage) =>
    availableCoverages.includes(coverage),
  );
};

const checkIsLeasing = (reasons: string[]): boolean => {
  return reasons.includes("IsLeasing");
};

const checkCannotHaveLiabilityAndReducedHasMortgagee = (
  availableCoverages: CarCoverage[],
  hasMortgagee: boolean,
): boolean => {
  return checkCanGetLiability(availableCoverages) && hasMortgagee;
};

const checkCannotHaveLiabilityAndReduced = (
  availableCoverages: CarCoverage[],
  hasMortgagee: boolean,
): boolean => {
  const canGetLiability = checkCanGetLiability(availableCoverages);
  const canGetReducedOwnDamage =
    checkCanGetReducedOwnDamage(availableCoverages);
  return !canGetLiability && !canGetReducedOwnDamage && !hasMortgagee;
};

const checkCannotGetLiability = (
  availableCoverages: CarCoverage[],
  reasons: string[],
  hasMortgagee: boolean,
): boolean => {
  const liabilityUnavailable = reasons.includes("CarYounger10Years");

  return (
    checkCanGetReducedOwnDamage(availableCoverages) &&
    liabilityUnavailable &&
    !hasMortgagee
  );
};

const checkCanGetLiability = (availableCoverages: CarCoverage[]): boolean => {
  return availableCoverages.includes(
    CarCoverage.AUTO_DRIVER_AND_PASSENGER_ACCIDENT,
  );
};
const checkCanGetReducedOwnDamage = (
  availableCoverages: CarCoverage[],
): boolean => {
  return availableCoverages.includes(CarCoverage.AUTO_REDUCED_OWN_DAMAGE);
};
